import dayjs, { type Dayjs } from 'dayjs';

export type DurationInputMode = 'milliseconds' | 'timeParts';

export type DurationParts = {
  hours: string;
  minutes: string;
  seconds: string;
  milliseconds: string;
};

export const emptyDurationParts: DurationParts = {
  hours: '',
  minutes: '',
  seconds: '',
  milliseconds: '',
};

export const areDurationPartsEqual = (left: DurationParts, right: DurationParts): boolean =>
  left.hours === right.hours &&
  left.minutes === right.minutes &&
  left.seconds === right.seconds &&
  left.milliseconds === right.milliseconds;

export const toTimestampDayjs = (value: Date | null | undefined): Dayjs | null =>
  value instanceof Date && !Number.isNaN(value.getTime()) ? dayjs(value) : null;

export const toDurationParts = (value: number | null | undefined): DurationParts => {
  if (value === null || value === undefined || !Number.isFinite(value) || value < 0) {
    return emptyDurationParts;
  }

  const normalizedValue = Math.floor(value);
  const hours = Math.floor(normalizedValue / 3_600_000);
  const minutes = Math.floor((normalizedValue % 3_600_000) / 60_000);
  const seconds = Math.floor((normalizedValue % 60_000) / 1000);
  const milliseconds = normalizedValue % 1000;

  return {
    hours: String(hours),
    minutes: String(minutes),
    seconds: String(seconds),
    milliseconds: String(milliseconds),
  };
};

const parseDurationPart = (value: string, maximum: number | null): number | null | 'invalid' => {
  const trimmedValue = value.trim();

  if (trimmedValue.length === 0) {
    return null;
  }

  if (!/^\d+$/.test(trimmedValue)) {
    return 'invalid';
  }

  const parsedValue = Number(trimmedValue);

  if (!Number.isFinite(parsedValue) || parsedValue < 0 || !Number.isInteger(parsedValue)) {
    return 'invalid';
  }

  if (maximum !== null && parsedValue > maximum) {
    return 'invalid';
  }

  return parsedValue;
};

export const toDurationMsFromParts = (parts: DurationParts): number | null | 'invalid' => {
  const parsedHours = parseDurationPart(parts.hours, null);
  const parsedMinutes = parseDurationPart(parts.minutes, 59);
  const parsedSeconds = parseDurationPart(parts.seconds, 59);
  const parsedMilliseconds = parseDurationPart(parts.milliseconds, 999);

  if (
    parsedHours === 'invalid' ||
    parsedMinutes === 'invalid' ||
    parsedSeconds === 'invalid' ||
    parsedMilliseconds === 'invalid'
  ) {
    return 'invalid';
  }

  if (
    parsedHours === null &&
    parsedMinutes === null &&
    parsedSeconds === null &&
    parsedMilliseconds === null
  ) {
    return null;
  }

  return (
    (parsedHours ?? 0) * 3_600_000 +
    (parsedMinutes ?? 0) * 60_000 +
    (parsedSeconds ?? 0) * 1000 +
    (parsedMilliseconds ?? 0)
  );
};
